import { useCallback } from 'react';
import { FormsV1 } from '@frontend/api-digital-forms';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../constants';
import { categorizeDraftError } from '../../draft/draft-errors';
import { toSaveDraftPayload } from '../../draft/draft-serializer';
import { DraftErrorCategory, DraftMetadata, DraftSnapshot } from '../../draft/draft.types';

export type VerifyDraftOwnerResult =
  | { type: 'verified'; verificationCode: string }
  | { type: 'mismatch' }
  /** Retry limit reached; takes precedence over mismatch (spec §5.3). */
  | { type: 'rate-limited'; cooldownSeconds: number }
  | { type: 'error'; category: DraftErrorCategory };

export type FetchDraftResult =
  | { type: 'success'; draft: FormsV1.Types.GetDraftIO['output'] }
  | { type: 'error'; category: DraftErrorCategory };

interface UseDraftApiArgs {
  companyId: string;
  submissionId: string;
}

/** Thin wrappers over the draft RPCs that return typed outcomes instead of throwing. */
export const useDraftApi = ({ companyId, submissionId }: UseDraftApiArgs) => {
  const { mutateAsync: verifyMutation } = FormsV1.Mutations.useVerifyPatientMobileAndBirthdateMutation({
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });
  const { mutateAsync: saveMutation } = FormsV1.Mutations.useSaveDraftMutation({
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });
  const { mutateAsync: deleteMutation } = FormsV1.Mutations.useDeleteDraftMutation({
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });
  const fetchGetDraft = FormsV1.Queries.useFetchGetDraft();

  /** `mobilePhone` is the last 4 digits; `birthdate` is `YYYY-MM-DD` (no timezone conversion). */
  const verifyOwner = useCallback(
    async ({ mobilePhone, birthdate }: { mobilePhone: string; birthdate: string }): Promise<VerifyDraftOwnerResult> => {
      try {
        const res = await verifyMutation({ companyId, submissionId, mobilePhone, birthdate });
        const cooldownSeconds = Number(res.remainingCooldownSeconds ?? 0);

        if (res.retryLimitReached || cooldownSeconds > 0) return { type: 'rate-limited', cooldownSeconds };
        if (res.isVerified && res.verificationCode) return { type: 'verified', verificationCode: res.verificationCode };
        return { type: 'mismatch' };
      } catch (error) {
        const { category } = categorizeDraftError(error);
        if (category === 'throttled') return { type: 'rate-limited', cooldownSeconds: 0 };
        return { type: 'error', category };
      }
    },
    [verifyMutation, companyId, submissionId]
  );

  const fetchDraft = useCallback(
    async (verificationCode: string): Promise<FetchDraftResult> => {
      try {
        const draft = await fetchGetDraft({
          request: { companyId, submissionId, verificationCode },
          options: { cacheTime: 0, staleTime: 0, retry: 1 },
          httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
        });
        return { type: 'success', draft };
      } catch (error) {
        return { type: 'error', category: categorizeDraftError(error).category };
      }
    },
    [fetchGetDraft, companyId, submissionId]
  );

  /** Rejects with the transport error so the autosave coordinator can categorize and retry it. */
  const saveDraft = useCallback(
    async (snapshot: DraftSnapshot, metadata: DraftMetadata) => {
      await saveMutation({ companyId, submissionId, ...toSaveDraftPayload(snapshot, metadata) });
    },
    [saveMutation, companyId, submissionId]
  );

  const deleteDraft = useCallback(async (): Promise<
    { type: 'success' } | { type: 'error'; category: DraftErrorCategory }
  > => {
    try {
      await deleteMutation({ companyId, submissionId });
      return { type: 'success' };
    } catch (error) {
      const { category } = categorizeDraftError(error);
      // Already gone counts as discarded.
      return category === 'not-found' ? { type: 'success' } : { type: 'error', category };
    }
  }, [deleteMutation, companyId, submissionId]);

  return { verifyOwner, fetchDraft, saveDraft, deleteDraft };
};
